import React, { useEffect, useRef, useState } from 'react';
import Wasp from './Wasp';
import { ADMIN_API_URL } from '../../config/adminApi';
import { fetchWithTimeout } from '../../utils/fetchWithTimeout';

const WASP_COUNT = 3;

// Filet de sécurité si le panel admin est injoignable — les textes réels
// viennent normalement de /api/wasp-messages (gérables depuis le panel,
// onglet Popup vacances).
const FALLBACK_TIP_MESSAGES = [
  "Nicolas est absent, j'en profite…",
  "Personne pour me chasser aujourd'hui.",
  "Tranquille, y'a plus un artisan dans le coin.",
];

const SIZE = 36; // px approximatif de l'emoji

// Une guêpe indépendante : sa propre vitesse, hauteur, délai et rythme de pause,
// tirés au sort une seule fois au montage — pour que plusieurs instances ne
// bougent jamais en miroir les unes des autres.
function WaspAgent({ messages }) {
  const flipRef = useRef(null);
  const tipRef  = useRef(null);

  useEffect(() => {
    const flip = flipRef.current;
    const tip  = tipRef.current;
    if (!flip || !tip) return;

    tip.textContent = messages[Math.floor(Math.random() * messages.length)];

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      flip.style.left = '16px';
      flip.style.top = '16px';
      flip.style.opacity = '1';
      return;
    }

    // Paramètres propres à cette guêpe, tirés une fois au montage.
    const dur     = 12000 + Math.random() * 8000;  // 12–20s pour traverser l'écran
    const delay   = Math.random() * 6000;           // départ décalé, 0–6s
    const pause   = 2500 + Math.random() * 4000;     // 2.5–6.5s d'attente entre deux passages
    const topBase = 0.15 + Math.random() * 0.6;      // hauteur entre 15% et 75% du viewport
    const topAmp  = 0.05 + Math.random() * 0.08;

    let goingRight = Math.random() < 0.5;
    let startTime  = null;
    let rafId      = null;
    let delayId    = null;
    let pauseId    = null;

    const handleEnter = () => { tip.style.opacity = '1'; };
    const handleLeave = () => { tip.style.opacity = '0'; };
    flip.addEventListener('mouseenter', handleEnter);
    flip.addEventListener('mouseleave', handleLeave);

    function frame(ts) {
      if (!startTime) startTime = ts;
      const t  = Math.min((ts - startTime) / dur, 1);
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const x = goingRight
        ? -SIZE * 2 + t * (vw + SIZE * 4)
        : vw + SIZE * 2 - t * (vw + SIZE * 4);
      const y = topBase * vh + Math.sin(t * Math.PI * 3) * topAmp * vh;

      flip.style.left = x + 'px';
      flip.style.top = y + 'px';
      flip.style.transform = goingRight ? 'scaleX(1)' : 'scaleX(-1)';

      const tipW = tip.offsetWidth;
      const tipX = Math.max(4, Math.min(x - tipW / 2 + SIZE / 2, vw - tipW - 4));
      tip.style.left = tipX + 'px';
      tip.style.top = (y - 36) + 'px';

      if (t < 1) {
        rafId = requestAnimationFrame(frame);
      } else {
        flip.style.opacity = '0';
        tip.style.opacity = '0';
        pauseId = setTimeout(() => {
          goingRight = !goingRight;
          startTime = null;
          flip.style.opacity = '1';
          rafId = requestAnimationFrame(frame);
        }, pause);
      }
    }

    flip.style.opacity = '0';
    delayId = setTimeout(() => {
      flip.style.opacity = '1';
      rafId = requestAnimationFrame(frame);
    }, delay);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(delayId);
      clearTimeout(pauseId);
      flip.removeEventListener('mouseenter', handleEnter);
      flip.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <>
      <div ref={flipRef} className="wandering-wasp" aria-hidden="true">
        <Wasp className="wandering-wasp-wings" />
      </div>
      <div ref={tipRef} className="wandering-wasp-tip"></div>
    </>
  );
}

// Easter egg décoratif, affiché sur tout le site (y compris par-dessus le popup
// vacances) tant qu'un popup vacances est actif. Plusieurs guêpes indépendantes
// plutôt qu'une seule, chacune avec son propre rythme. Les textes viennent du
// panel admin (un seul fetch ici, partagé par toutes les instances plutôt que
// répété par guêpe) — repli sur les 3 messages par défaut si l'API est injoignable.
export default function WanderingWasp() {
  const [messages, setMessages] = useState(FALLBACK_TIP_MESSAGES);

  useEffect(() => {
    fetchWithTimeout(`${ADMIN_API_URL}/api/wasp-messages`)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setMessages(data.map((m) => m.message));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <>
      {Array.from({ length: WASP_COUNT }).map((_, i) => (
        <WaspAgent key={i} messages={messages} />
      ))}
    </>
  );
}
